// The READ + ATTEST layer for the golden sources already landed on a period.
//
// WHY THIS EXISTS. Until now the only way to attest a source was the transient "what was
// landed" panel inside CombinedCsvDropZone — it renders off local `results` state, so a page
// reload destroyed it and with it every attest button on the tab. Eight sources were landed for
// 2026-07 and NONE of them could be attested through the UI, which is the whole of what was
// holding the cycle's snapshot gate shut. This hook reads the sources back from the server so
// the control survives a reload, a different laptop, and a different person.
//
// WHY IT READS THE TABLE DIRECTLY AND NOT reporting_golden_source_summary(). The summary RPC
// exists and is SECURITY DEFINER, but it does NOT return the row `id` — and the id is exactly
// what attest_golden_source takes. It also drops original_filename, byte_size and
// attestation_note, which are the provenance an attester needs in order to know what they are
// signing for. The table carries its own RLS policy for precisely this audience
// ("Reporting audience reads golden sources", FOR SELECT TO authenticated USING
// is_investment_team() OR is_operations_team() OR is_admin_or_superadmin(auth.uid())), so a
// direct select is congruent with the RPC, not a way round it.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/** A row of public.reporting_golden_sources, as this surface needs it. */
export interface GoldenSourceRow {
  id: string;
  period_label: string;
  cycle_id: string | null;
  kind: string;
  source_system: string;
  basis: string;
  as_of_date: string | null;
  /**
   * For an FE drop this is the uploaded file's name. For a native extract the ingest writes a
   * SYNTHETIC descriptor instead — "supabase:<table>+<table>@<extracted-at>" — because there
   * was no file (reporting-supabase-ingest/index.ts:1235-1239). Rendered as provenance, never
   * as "the filename", so the two cases cannot be confused.
   */
  original_filename: string | null;
  storage_path: string | null;
  content_sha256: string;
  byte_size: number | null;
  row_count: number | null;
  ingest_status: string;
  reject_reason: string | null;
  attested_by: string | null;
  attested_at: string | null;
  /**
   * ⚠️ NOT the attester's note, despite the name. This column is written by the INGEST
   * (reporting-supabase-ingest/index.ts:643-645, 1263) and describes the SOURCE — the reducer,
   * the base date, the same-origin caveat. attest_golden_source never writes it; the attester's
   * own note goes to reporting_approvals.note. Label it as an ingest-recorded caveat wherever
   * it is shown, or a reader will take a machine-written default for a human's vouch.
   */
  attestation_note: string | null;
  created_at: string;
}

const SOURCE_COLS =
  "id, period_label, cycle_id, kind, source_system, basis, as_of_date, original_filename, " +
  "storage_path, content_sha256, byte_size, row_count, ingest_status, reject_reason, " +
  "attested_by, attested_at, attestation_note, created_at";

export function goldenSourcesKey(periodLabel: string | null | undefined) {
  return ["reporting_golden_sources", periodLabel ?? null] as const;
}

/**
 * Every non-superseded golden source landed for this period.
 *
 * Superseded rows are excluded to match what reporting_snapshot_precheck counts — it filters
 * `ingest_status <> 'superseded'` on every one of its present/validated/attested passes, so
 * showing them here would put rows on screen that the gate is not looking at.
 */
export function useGoldenSources(periodLabel: string | null | undefined, enabled = true) {
  const label = (periodLabel ?? "").trim();
  return useQuery({
    queryKey: goldenSourcesKey(label),
    enabled: enabled && label.length > 0,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<GoldenSourceRow[]> => {
      // Cast through `any`: integrations/supabase/types.ts has not been regenerated since the
      // 0003a reporting DDL, so the client's table union does not contain
      // reporting_golden_sources. Same reason useReportingAccess casts its rpc call. The RLS
      // policy is the real wall either way; regen types and this cast can drop.
      const { data, error } = await (supabase as any)
        .from("reporting_golden_sources")
        .select(SOURCE_COLS)
        .eq("period_label", label)
        .neq("ingest_status", "superseded")
        .order("kind", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as GoldenSourceRow[];
    },
  });
}

/**
 * The precheck envelope, verbatim from reporting_snapshot_precheck(period_label).
 *
 * This is the AUTHORITY on which kinds are required — the required set is derived server-side
 * from the cycle's period_type (monthly-interim takes four kinds, quarterly-full takes eight),
 * and it has already moved once (0003n). Hardcoding it in the client would mean the panel and
 * the gate could disagree about what the cycle needs, which is the one thing this panel exists
 * to prevent.
 */
export interface SnapshotPrecheck {
  period_label: string;
  period_type: string;
  required_kinds: string[];
  present_kinds: string[];
  source_guard_pass: boolean;
  returns_fe_only_pass: boolean;
  required_present: boolean;
  all_validated: boolean;
  all_attested: boolean;
  missing_required: string[];
  unvalidated_required: string[];
  unattested_required: string[];
  rejected_kinds: string[];
  ready: boolean;
}

/**
 * The FE export census, verbatim from reporting_fe_export_census(period_label).
 *
 * WHY THIS IS SEPARATE FROM THE PRECHECK. `reporting_snapshot_precheck` asks "is this KIND
 * present?" and never "which WINDOWS of it are present?". One FE Performance Table export
 * satisfies `fe_benchmark_returns`, so an operator who drops a single file sees `ready` and
 * has no way to learn that three more exports are still required — until the render refuses
 * on `unresolved_cell_ratio` weeks later, phrased as a rendering problem.
 *
 * Measured on cycle ec8e622a (2026-07): the benchmark source landed 26 rows, every one
 * `return_1m`, while the portfolio side carried all four metrics because it also arrived on
 * the CIO sheet transport. 105 of that cycle's 118 unresolved cells were the benchmark and
 * excess cells for q2/m6/y1, and nothing in the UI said a file was missing.
 *
 * DESCRIPTIVE ONLY. This adds nothing to `missing_required` and cannot change `ready` — the
 * gate verdict is still entirely the precheck's. It exists so the operator is told.
 */
export interface FeExportWindow {
  metric: string;
  window_months: number;
  window_start: string;
  window_end: string;
  /** The date to type into FE: the PRIOR priced date, one day before window_start. */
  fe_price_range_from: string;
  fe_price_range_to: string;
  present: boolean;
  fe_kinds: string[];
  kinds_any_source: string[];
  missing_kinds: string[];
}

/**
 * Which single export defined `fe_kind_roster`.
 *
 * The roster is measured from ONE export — the most recently landed export of the primary
 * window — and not from the union across the period's history. 20260820203412 took the union
 * and, on 2026-07, held every window to a 5-kind roster because an export from six days
 * earlier had come from a different FE report template: the panel reported "1 of 4 complete"
 * on a period whose four exports had all landed. This field names the file that set the
 * roster, so a roster that looks wrong can be traced without opening the database.
 *
 * `null` when no FE export of the primary window has landed at all.
 */
export interface FeKindRosterSource {
  content_sha256: string;
  filename: string | null;
  landed_at: string;
}

export interface FeExportCensus {
  period_label: string;
  period_type: string;
  as_of_date: string;
  primary_metric: string;
  fe_kind_roster: string[];
  fe_kind_roster_source: FeKindRosterSource | null;
  exports_required: number;
  exports_present: number;
  missing_exports: string[];
  exports: FeExportWindow[];
  operator_note: string;
}

export function feExportCensusKey(periodLabel: string | null | undefined) {
  return ["reporting_fe_export_census", periodLabel ?? null] as const;
}

export function useFeExportCensus(periodLabel: string | null | undefined, enabled = true) {
  const label = (periodLabel ?? "").trim();
  return useQuery({
    queryKey: feExportCensusKey(label),
    enabled: enabled && label.length > 0,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<FeExportCensus> => {
      // Same `any` cast as useSnapshotPrecheck below: the generated types do not carry the
      // reporting RPCs.
      const { data, error } = await (supabase as any).rpc("reporting_fe_export_census", {
        _period_label: label,
      });
      if (error) throw error;
      return data as FeExportCensus;
    },
  });
}

export function snapshotPrecheckKey(periodLabel: string | null | undefined) {
  return ["reporting_snapshot_precheck", periodLabel ?? null] as const;
}

export function useSnapshotPrecheck(periodLabel: string | null | undefined, enabled = true) {
  const label = (periodLabel ?? "").trim();
  return useQuery({
    queryKey: snapshotPrecheckKey(label),
    enabled: enabled && label.length > 0,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<SnapshotPrecheck> => {
      // Cast through `any`: the generated Supabase types do not know the reporting RPCs yet —
      // the same pattern useReportingAccess uses for can_review_reporting.
      const { data, error } = await (supabase as any).rpc("reporting_snapshot_precheck", {
        _period_label: label,
      });
      if (error) throw error;
      return data as SnapshotPrecheck;
    },
  });
}

/**
 * The ATTEST entries of the approvals ledger, keyed by the source they signed.
 *
 * WHY THIS IS NEEDED. attest_golden_source writes the attester's note to
 * reporting_approvals.note and NOT to the source row (it never touches
 * reporting_golden_sources.attestation_note — see the RPC body). The source row therefore
 * carries `attested_by`/`attested_at` and nothing about the BASIS on which it was signed. An
 * attestation whose stated basis is unreadable in the UI is an audit record in name only, so
 * the panel reads the ledger back and shows the note beside the signature.
 *
 * It is readable by exactly this audience: reporting_approvals' only SELECT policy is
 * "Reporting audience reads approvals" — FOR SELECT TO authenticated USING
 * can_review_reporting() — which is the same predicate useReportingAccess.canReview reads. The
 * table is append-only (trg_reporting_approvals_no_mutate refuses UPDATE and DELETE), so what
 * is shown here is what was recorded, permanently.
 */
export interface SourceAttestation {
  id: string;
  source_id: string;
  actor: string | null;
  actor_team: string | null;
  note: string | null;
  created_at: string;
}

export function sourceAttestationsKey(cycleId: string | null | undefined) {
  return ["reporting_approvals_attest", cycleId ?? null] as const;
}

/**
 * Returns attest-ledger entries for a cycle, newest first, grouped by source id.
 *
 * More than one entry for a source is possible and is NOT hidden: the RPC's UPDATE is
 * unconditional, so a second attestation overwrites attested_by/attested_at on the row while
 * leaving both ledger rows in place. The ledger is the only place that history survives, which
 * is why the panel surfaces the count rather than just the latest.
 */
export function useSourceAttestations(cycleId: string | null | undefined, enabled = true) {
  const id = (cycleId ?? "").trim();
  return useQuery({
    queryKey: sourceAttestationsKey(id),
    enabled: enabled && id.length > 0,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<Map<string, SourceAttestation[]>> => {
      // Cast through `any` for the same reason as above: the generated Supabase types predate
      // the 0003a reporting DDL and do not carry these tables.
      const { data, error } = await (supabase as any)
        .from("reporting_approvals")
        .select("id, actor, actor_team, note, detail, created_at")
        .eq("cycle_id", id)
        .eq("action", "attest")
        .order("created_at", { ascending: false });
      if (error) throw error;

      const out = new Map<string, SourceAttestation[]>();
      for (const r of (data ?? []) as any[]) {
        const sourceId = r?.detail?.source_id;
        if (typeof sourceId !== "string" || !sourceId) continue;
        const entry: SourceAttestation = {
          id: r.id,
          source_id: sourceId,
          actor: r.actor ?? null,
          actor_team: r.actor_team ?? null,
          note: r.note ?? null,
          created_at: r.created_at,
        };
        const list = out.get(sourceId);
        if (list) list.push(entry);
        else out.set(sourceId, [entry]);
      }
      return out;
    },
  });
}

/**
 * Attest ONE golden source.
 *
 * The RPC is attest_golden_source(_source_id uuid, _note text DEFAULT NULL) RETURNS void,
 * SECURITY DEFINER. It takes the actor from auth.uid() server-side, refuses anyone outside
 * Investment ∪ Operations, sets attested_by/attested_at on the row, and appends an 'attest'
 * row to reporting_approvals carrying the note. attested_by is NEVER written directly from
 * the client — the column has a FK to auth.users and a CHECK tying it to attested_at, and the
 * whole point of the RPC is that the actor is the server's idea of who called it, not the
 * client's claim about who it is.
 *
 * WHERE THE NOTE LANDS. In reporting_approvals.note, NOT in
 * reporting_golden_sources.attestation_note — the RPC does not touch that column. So the note
 * is recoverable from the approvals ledger (which is append-only: trg_reporting_approvals_no_mutate
 * refuses UPDATE and DELETE) but it will NOT appear on the source row. Callers that want to
 * show "what this person vouched for" must read the ledger — useSourceAttestations does.
 *
 * ⚠️ THE RPC DOES NOT REFUSE A SECOND ATTESTATION. Its UPDATE is unconditional —
 * `SET attested_by = $1, attested_at = now() WHERE id = $2` with no guard on attested_by being
 * NULL — so re-attesting silently REPLACES the first signature on the row. Only the ledger
 * keeps the original. The panel therefore withholds the control on an already-attested source,
 * but that is a CLIENT-side guard: it is the UI, not the database, that is preventing the
 * overwrite. Making it safe would need a `WHERE ... AND attested_by IS NULL` (or an explicit
 * re-attest path that records supersession) in the RPC itself.
 */
export function useAttestGoldenSource(periodLabel: string | null | undefined, cycleId?: string | null) {
  const qc = useQueryClient();
  const label = (periodLabel ?? "").trim();

  return useMutation({
    mutationFn: async ({ sourceId, note }: { sourceId: string; note: string }) => {
      const trimmed = (note ?? "").trim();
      // Refuse an empty note in the CLIENT as well as asking for one in the UI. The RPC
      // defaults _note to NULL and would happily record an attestation that says nothing —
      // and reporting_approvals is append-only, so a blank vouch can never be corrected
      // afterwards. An attestation with no stated basis is the thing this panel is trying
      // not to produce.
      if (!trimmed) {
        throw new Error(
          "An attestation needs a stated basis. Say what you are vouching for before signing — " +
            "the approvals ledger is append-only and cannot be corrected later.",
        );
      }
      const { error } = await (supabase as any).rpc("attest_golden_source", {
        _source_id: sourceId,
        _note: trimmed,
      });
      if (error) throw error;
      return { sourceId };
    },
    onSuccess: () => {
      // Re-read from the server rather than patching local state: attestation changes the
      // precheck verdict too, and a panel that only remembered its own click would show a
      // second reviewer a stale gate.
      qc.invalidateQueries({ queryKey: goldenSourcesKey(label) });
      qc.invalidateQueries({ queryKey: snapshotPrecheckKey(label) });
      if (cycleId) qc.invalidateQueries({ queryKey: sourceAttestationsKey(cycleId) });
      if (cycleId) qc.invalidateQueries({ queryKey: ["get_reporting_cycle", cycleId] });
      qc.invalidateQueries({ queryKey: ["list_reporting_cycles"] });
    },
  });
}
